// Customers.jsx — the people who order.
//
// ── A BUG THIS FIXES ──
// Like the notification screen, the one this replaces imported useEffect and
// never called it: no mount fetch, no page fetch, no search fetch. The list was
// pulled only as a side effect of saving or deleting a customer, so the page
// opened empty, searching did nothing, and the page numbers moved without the
// rows changing.
//
// ── THERE IS NO DELETE HERE ──
// Erasure is the customer's own act: POST /customer/delete_my_data with their
// token. It is irreversible, cascades to their addresses, and under the DPDP
// Act it is their right to exercise rather than the shop's to perform. An
// admin-facing bin turns that into a staff convenience with no undo. Blocking
// is the operational equivalent and can be taken back.
//
// ── IDS ──
// This slice still speaks `_id`, not `id`. Every other screen converted so far
// is on `id`; this one is left as it is because changing it means changing the
// slice and the three endpoints behind it, which is a separate job from making
// the screen work.

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  fetchCustomers,
  updateCustomer,
  toggleCustomerStatus,
} from "../../../slice/customer/customerslice";
import {
  PageShell,
  DataTable,
  Avatar,
  Toggle,
  RowActions,
  Modal,
  Field,
  Button,
  Pager,
} from "../component/AdminUi";

const PER_PAGE = 10;

const onDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

/* "2h ago" while it is recent, a date once it is not. A relative stamp is
   easier to judge at a glance, and useless past a day or two. */
const lastSeen = (iso) => {
  if (!iso) return "Never";
  const seconds = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (seconds < 60) return "Just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return onDate(iso);
};

const looksLikeEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

export default function Customers() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { customers, loading, error, pagination } = useSelector(
    (s) => s.customer
  );

  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState("");

  /* Confirmed rather than done on the click, because switching a customer off
     stops them ordering and there is no undo on the row. */
  const [confirming, setConfirming] = useState(null);

  /* The effect the old screen was missing. */
  useEffect(() => {
    dispatch(fetchCustomers({ page, limit: PER_PAGE, search: query }));
  }, [dispatch, page, query]);

  const reload = () =>
    dispatch(fetchCustomers({ page, limit: PER_PAGE, search: query }));

  /* ── editing ───────────────────────────────────────────────────────────── */

  const openEdit = (row) => {
    setEditing(row);
    setName(row.name || "");
    setEmail(row.email || "");
    setProblem("");
  };

  const close = () => {
    setEditing(null);
    setName("");
    setEmail("");
    setProblem("");
  };

  const save = async () => {
    if (!name.trim()) return setProblem("Give them a name.");
    if (!email.trim()) return setProblem("An email address is required.");
    if (!looksLikeEmail(email.trim()))
      return setProblem("That does not look like an email address.");

    setSaving(true);
    setProblem("");
    try {
      await dispatch(
        updateCustomer({
          id: editing._id,
          data: { name: name.trim(), email: email.trim() },
        })
      ).unwrap();
      reload();
      close();
    } catch (err) {
      setProblem(err || "That did not save.");
    } finally {
      setSaving(false);
    }
  };

  /* ── the two confirmed actions ─────────────────────────────────────────── */

  const askToggle = (row) => {
    const on = row.status !== false;
    setConfirming({
      kind: "toggle",
      row,
      title: on
        ? `Block ${row.name || "this customer"}?`
        : `Let ${row.name || "this customer"} back in?`,
      body: on
        ? "They will not be able to sign in or place an order until this is turned back on. Anything already in their basket stays there."
        : "They will be able to sign in and order again straight away.",
      verb: on ? "Block them" : "Allow them",
    });
  };

  const doConfirmed = async () => {
    if (!confirming) return;
    const { row } = confirming;
    setConfirming(null);
    try {
      await dispatch(toggleCustomerStatus(row._id)).unwrap();
    } catch (err) {
      setProblem(err || "That did not go through.");
    }
  };

  const rows = customers || [];

  /* ── render ────────────────────────────────────────────────────────────── */

  return (
    <PageShell
      title="Customers"
      count={
        loading && !rows.length
          ? "Loading…"
          : pagination?.total != null
          ? `${pagination.total} registered`
          : `${rows.length} shown`
      }
      search={{
        value: query,
        onChange: (v) => {
          setQuery(v);
          setPage(1);
        },
        placeholder: "Search name, number or email",
      }}
    >
      <DataTable
        columns={["Customer", "Phone", "Email", "Can order", "Joined", "Last seen", ""]}
        loading={loading}
        error={error || problem}
        isEmpty={!loading && rows.length === 0}
        emptyTitle={query ? "Nobody matches that" : "No customers yet"}
        emptyHint={
          query
            ? "Try the phone number instead."
            : "Accounts appear here once someone signs in to the app."
        }
      >
        {rows.map((row) => {
          const canOrder = row.status !== false;
          return (
            <tr key={row._id}>
              <td>
                <span className="ui-who">
                  <Avatar label={row.name} />
                  <span className="ui-who__name">{row.name || "—"}</span>
                </span>
              </td>
              <td className="ui-num">
                {row.phoneNumber || row.phone ? (
                  (row.phoneNumber || row.phone).replace(
                    /^(\d{5})(\d{5})$/,
                    "$1 $2"
                  )
                ) : (
                  <span className="ui-muted">not set</span>
                )}
              </td>
              <td>
                {row.email || <span className="ui-muted">not set</span>}
              </td>
              <td>
                <Toggle
                  on={canOrder}
                  onChange={() => askToggle(row)}
                  label={canOrder ? "Can order — tap to block" : "Blocked — tap to allow"}
                />
              </td>
              <td className="ui-num">{onDate(row.createdAt)}</td>
              <td className="ui-num ui-muted">{lastSeen(row.lastLoginAt)}</td>
              <td>
                <RowActions
                  onView={() =>
                    navigate(`/admin/customers/${row._id}/orders`, {
                      state: { customer: row },
                    })
                  }
                  onEdit={() => openEdit(row)}
                />
              </td>
            </tr>
          );
        })}
      </DataTable>

      <Pager
        page={page}
        totalPages={pagination?.totalPages}
        total={pagination?.total}
        perPage={PER_PAGE}
        onPage={setPage}
      />

      <Modal
        open={!!editing}
        title={`Edit ${editing?.name || "customer"}`}
        onClose={close}
        footer={
          <>
            <Button onClick={close}>Cancel</Button>
            <Button kind="primary" onClick={save} disabled={saving}>
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </>
        }
      >
        {problem ? <div className="ui-alert ui-alert--error">{problem}</div> : null}

        <Field label="Name" required>
          <input
            className="ui-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>

        <Field
          label="Email"
          required
          hint="The phone number is the account and cannot be edited here."
        >
          <input
            className="ui-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
      </Modal>

      <Modal
        open={!!confirming}
        title={confirming?.title || ""}
        onClose={() => setConfirming(null)}
        footer={
          <>
            <Button onClick={() => setConfirming(null)}>Cancel</Button>
            <Button kind="primary" onClick={doConfirmed}>
              {confirming?.verb}
            </Button>
          </>
        }
      >
        <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55 }}>
          {confirming?.body}
        </p>
      </Modal>
    </PageShell>
  );
}
